// Settings → Tool Palette panel (EXPERIMENT / mockup).
//
// Per-org button-visibility picker. Operator toggles which of the 9
// catalog buttons appear on every document detail page in this org.
// State persists to localStorage via lib/tool-palette-config.ts so
// the next mount of <ToolPalette /> picks up the change. Backend
// persistence is deferred (FR 2026-05-29 — "Configurable per-org" is
// flagged "biggest scope-creep for an experiment"; v1 stays
// client-only so the UX can be iterated cheaply).

import { useEffect, useMemo, useRef, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { GripVertical, Pencil, Check } from 'lucide-react';
import { toast } from './Toast.js';
import { useSession } from '../hooks/useSession.js';
import { useToolActions } from '../hooks/useToolActions.js';
import {
  TOOL_PALETTE_BUTTONS,
  applyPaletteOrder,
  defaultPaletteConfig,
  loadPaletteConfig,
  loadPaletteOrder,
  savePaletteConfig,
  savePaletteOrder,
  type ToolPaletteConfig,
} from '../lib/tool-palette-config.js';

export function ToolPalettePanel() {
  const { session } = useSession();
  const orgSlug = session?.activeOrg?.slug ?? '';
  const orgName = session?.activeOrg?.name ?? orgSlug;
  const [config, setConfig] = useState<ToolPaletteConfig>(() =>
    orgSlug ? loadPaletteConfig(orgSlug) : defaultPaletteConfig(),
  );
  const [dirty, setDirty] = useState(false);
  // Operator-curated ordering (drag-to-reorder, FR 2026-05-31).
  // Empty array = catalog order (no override saved). Editing is
  // gated behind `editMode` so a stray click can't reorder anything
  // by accident.
  const [order, setOrder] = useState<string[]>(() =>
    orgSlug ? loadPaletteOrder(orgSlug) : [],
  );
  const [editMode, setEditMode] = useState(false);
  // Ref instead of state — the dragged index doesn't need to drive
  // a re-render; using state would cause one per dragover event.
  const dragFromIdx = useRef<number | null>(null);
  // CTSK-121 — visual-only mirror of dragFromIdx: drives the
  // `grabbing` cursor + lifted look on the row being dragged. State
  // (not ref) because it must re-render, but it only changes on drag
  // start/end — never per dragover, so the original perf concern
  // above still holds.
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);

  // Re-read when the active org changes (org switcher mid-session).
  useEffect(() => {
    if (!orgSlug) return;
    setConfig(loadPaletteConfig(orgSlug));
    setOrder(loadPaletteOrder(orgSlug));
    setDirty(false);
  }, [orgSlug]);

  function toggle(key: string) {
    setConfig((c) => ({ ...c, [key]: !(c[key] !== false) }));
    setDirty(true);
  }

  function handleSave() {
    if (!orgSlug) {
      toast.error('Cannot save palette config without an active org.');
      return;
    }
    savePaletteConfig(orgSlug, config);
    // Order also persists here so the "Save" button is the single
    // affordance — drag-to-reorder already wrote to localStorage on
    // each drop, but a final save keeps the ordering aligned with
    // the visibility map even if the operator hit Reset.
    savePaletteOrder(orgSlug, order);
    setDirty(false);
    toast.success(`Tool Palette saved for ${orgName}.`);
  }

  function handleReset() {
    setConfig(defaultPaletteConfig());
    setDirty(true);
  }

  function handleResetOrder() {
    setOrder([]);
    if (orgSlug) savePaletteOrder(orgSlug, []);
    toast.info('Tool Palette order reset to default.');
  }

  // ─── Drag-and-drop reorder (HTML5 native; no extra deps) ────────
  //
  // The picker walks `rows` once to build a stable ordered list; we
  // mutate `order` (the saved key sequence) on each drop. ToolPalette
  // re-reads localStorage on next mount so the change shows up the
  // next time the operator visits a document.

  function commitReorder(fromIdx: number, toIdx: number) {
    if (fromIdx === toIdx) return;
    const orderedKeys = rows.map((r) => r.key);
    const [moved] = orderedKeys.splice(fromIdx, 1);
    if (!moved) return;
    orderedKeys.splice(toIdx, 0, moved);
    setOrder(orderedKeys);
    if (orgSlug) savePaletteOrder(orgSlug, orderedKeys);
  }

  // FR 2026-05-31 — merge hardcoded mockup buttons with tool-derived
  // actions from installed manifests. The picker renders both
  // sources in one flat list so the operator manages visibility for
  // every button from a single place.
  const { actions: toolActions } = useToolActions();
  type Row = {
    key: string;
    label: string;
    description: string;
    icon: LucideIcon;
    source: 'mockup' | 'tool';
    toolSlug?: string;
    ready: boolean;
    verb: 'mockup' | 'tab' | 'invoke';
    mvp: boolean;
  };
  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    for (const b of TOOL_PALETTE_BUTTONS) {
      out.push({
        key: b.key,
        label: b.label,
        description: b.description,
        icon: b.icon,
        source: 'mockup',
        ready: b.ready,
        verb: 'mockup',
        mvp: b.mvp,
      });
    }
    for (const a of toolActions) {
      out.push({
        key: a.key,
        label: a.label,
        description: a.description ?? '',
        icon: a.icon,
        source: 'tool',
        toolSlug: a.toolSlug,
        ready: a.ready,
        verb: a.verb === 'mockup' ? 'mockup' : a.verb,
        mvp: false,
      });
    }
    // Apply the saved order — keys present in `order` come first in
    // that sequence; new ones (newly-installed tool buttons) append
    // at the tail.
    return applyPaletteOrder(out, order);
  }, [toolActions, order]);

  const enabledCount = rows.filter((r) => config[r.key] !== false).length;

  return (
    <section
      style={{
        background: 'var(--surface-raised)',
        padding: 20,
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-subtle)',
        maxWidth: 720,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 16 }}>Tool Palette</h2>
          <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 13 }}>
            Pick which action buttons appear above every document detail page in <strong>{orgName}</strong>.
            {' '}
            <span
              style={{
                display: 'inline-block',
                marginLeft: 4,
                padding: '1px 6px',
                fontSize: 10,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: 'var(--warning-primary)',
                background: 'color-mix(in srgb, var(--warning-primary) 14%, transparent)',
                borderRadius: 999,
                verticalAlign: 'middle',
              }}
              title="Mockup — config persists in browser storage only, not yet backed by the API."
            >
              Mockup
            </span>
          </p>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
          {enabledCount}/{rows.length} buttons visible
        </div>
      </div>

      {editMode && (
        <div
          style={{
            margin: '0 0 10px',
            padding: '6px 10px',
            background: 'color-mix(in srgb, var(--accent-primary) 8%, var(--surface-canvas))',
            border: '1px dashed var(--accent-primary)',
            borderRadius: 'var(--radius-md)',
            fontSize: 11,
            color: 'var(--accent-primary)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <GripVertical size={12} aria-hidden />
          <span>Drag the handles to reorder buttons. Changes save automatically.</span>
          <button
            type="button"
            onClick={handleResetOrder}
            style={{
              marginLeft: 'auto',
              padding: '2px 8px',
              fontSize: 11,
              fontWeight: 600,
              background: 'transparent',
              border: '1px solid var(--accent-primary)',
              borderRadius: 999,
              color: 'var(--accent-primary)',
              cursor: 'pointer',
            }}
          >
            Reset order
          </button>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
        {rows.map((r, idx) => {
          const enabled = config[r.key] !== false;
          const Icon = r.icon;
          return (
            <label
              key={r.key}
              draggable={editMode}
              onDragStart={(e) => {
                if (!editMode) return;
                dragFromIdx.current = idx;
                setDraggingIdx(idx);
                e.dataTransfer.effectAllowed = 'move';
                // Some browsers ignore drag without setData.
                e.dataTransfer.setData('text/plain', r.key);
              }}
              onDragOver={(e) => {
                if (!editMode || dragFromIdx.current === null) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
              }}
              onDrop={(e) => {
                if (!editMode || dragFromIdx.current === null) return;
                e.preventDefault();
                commitReorder(dragFromIdx.current, idx);
                dragFromIdx.current = null;
                setDraggingIdx(null);
              }}
              onDragEnd={() => { dragFromIdx.current = null; setDraggingIdx(null); }}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '8px 10px',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-subtle)',
                background: enabled ? 'var(--surface-canvas)' : 'transparent',
                // CTSK-121 — grab on hover, grabbing while the row is
                // actually in flight; the dragged row dims slightly so
                // the operator can track what they picked up. Behavior
                // (HTML5 drag events, commitReorder) is untouched.
                cursor: editMode ? (draggingIdx === idx ? 'grabbing' : 'grab') : 'pointer',
                opacity: draggingIdx === idx ? 0.6 : 1,
              }}
            >
              {editMode && (
                <span
                  aria-hidden
                  title="Drag to reorder"
                  style={{
                    // CTSK-121 drag-handle refinement: centered against
                    // the row's first text line (align with the checkbox
                    // + icon), a real padded hit target instead of a
                    // bare glyph, and cursor states that read as a
                    // handle (grab → grabbing while dragging).
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    alignSelf: 'flex-start',
                    padding: '2px 2px',
                    marginTop: 1,
                    borderRadius: 4,
                    color: 'var(--text-muted)',
                    cursor: draggingIdx === idx ? 'grabbing' : 'grab',
                  }}
                >
                  <GripVertical size={14} />
                </span>
              )}
              <input
                type="checkbox"
                checked={enabled}
                onChange={() => toggle(r.key)}
                style={{ marginTop: 3 }}
                // In edit mode we still allow toggling visibility;
                // clicking the checkbox doesn't initiate a drag.
                onClick={(e) => e.stopPropagation()}
              />
              <Icon size={16} aria-hidden style={{ marginTop: 3, color: 'var(--text-secondary)' }} />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{r.label}</span>
                  {r.mvp && (
                    <span
                      title="In the FR-recommended MVP-4 cut."
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        color: 'var(--accent-primary)',
                        background: 'color-mix(in srgb, var(--accent-primary) 14%, transparent)',
                        padding: '1px 6px',
                        borderRadius: 999,
                      }}
                    >
                      MVP
                    </span>
                  )}
                  {r.source === 'tool' && (
                    <span
                      title={`Contributed by the '${r.toolSlug}' tool install. Manage installs in Settings → Tools.`}
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        color: 'var(--accent-primary)',
                        background: 'color-mix(in srgb, var(--accent-primary) 10%, transparent)',
                        padding: '1px 6px',
                        borderRadius: 999,
                      }}
                    >
                      Tool · {r.toolSlug}
                    </span>
                  )}
                  {r.verb === 'tab' && (
                    <span
                      title="Opens in a new tab. The browser handles navigation; no proxy call needed."
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        color: 'var(--success-primary, var(--accent-primary))',
                        background: 'color-mix(in srgb, var(--success-primary, var(--accent-primary)) 14%, transparent)',
                        padding: '1px 6px',
                        borderRadius: 999,
                      }}
                    >
                      Link
                    </span>
                  )}
                  {!r.ready && (
                    <span
                      title="No real backend wiring yet. Clicking the button fires a 'coming soon' toast."
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        color: 'var(--text-muted)',
                        background: 'var(--surface-sunken)',
                        padding: '1px 6px',
                        borderRadius: 999,
                      }}
                    >
                      Soon
                    </span>
                  )}
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                    {r.key}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.45 }}>
                  {r.description}
                </div>
              </div>
            </label>
          );
        })}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
        <button
          type="button"
          className="btn btn--primary"
          onClick={handleSave}
          disabled={!dirty}
        >
          Save
        </button>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={handleReset}
        >
          Reset to default (all on)
        </button>
        {dirty && (
          <span style={{ alignSelf: 'center', fontSize: 12, color: 'var(--warning-primary)' }}>
            Unsaved changes
          </span>
        )}
        {/* Reorder / Done sits on the right of the same row as Save +
            Reset (moved here from the header on dr.the0's ask). Same
            visual weight as the other action buttons. */}
        <button
          type="button"
          onClick={() => setEditMode((m) => !m)}
          title={editMode ? 'Done reordering' : 'Reorder buttons (drag to rearrange)'}
          aria-label={editMode ? 'Done reordering' : 'Reorder buttons'}
          aria-pressed={editMode}
          style={{
            marginLeft: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            height: 32,
            borderRadius: 'var(--radius-md)',
            border: `1px solid ${editMode ? 'var(--accent-primary)' : 'var(--border-strong)'}`,
            background: editMode
              ? 'color-mix(in srgb, var(--accent-primary) 12%, var(--surface-raised))'
              : 'var(--surface-raised)',
            color: editMode ? 'var(--accent-primary)' : 'var(--text-primary)',
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: editMode ? 600 : 400,
          }}
        >
          {editMode ? <Check size={14} aria-hidden /> : <Pencil size={14} aria-hidden />}
          <span>{editMode ? 'Done' : 'Reorder'}</span>
        </button>
      </div>
    </section>
  );
}
